import { Component, Input, AfterViewInit, OnDestroy, OnChanges, Output, EventEmitter, signal } from '@angular/core';
import { UntypedFormControl, Validators } from '@angular/forms';
import { createForm, FormType, subformComponentProviders } from 'ngx-sub-form';
import { catchError, map } from 'rxjs';
import { UserUnitsModel } from 'src/app/core/components/user-units/user-units.model';
import { Flow } from 'src/app/perical/models/flow.model';
import { Fluid, FluidType } from 'src/app/perical/models/fluid.model';
import { MudPit, OperationType, TripPipeAndCirculate } from 'src/app/perical/models/thermal-operation.model';
import { GetShortLengthValueFromInches } from 'src/app/perivis/shared/helpers/units.helper';
import { PeriforOnChangeMessages, SignalRService } from 'src/app/shared/services/signal-r.service';
import { TubularString } from 'src/app/wellbore-inputs/models/well-schematic.model';
import { DrillstringService } from 'src/app/wellbore-inputs/services/drillstring.service';

export interface TripPipeAndCirculateForm {
  type: OperationType;
  fluid: Fluid;
  standardFluidFlow: Flow;
  tripIntoHole: number;
  circulationTime: number;
  includeRiser: boolean;
  outsideDiameter: number;
  insideDiameter: number;
  inletTemperature: number;
  pipeRoughness: number;
  isPullOutOfHole: boolean;
  isNextStringInPlace: boolean;
  pullOutOfHoleTime: number;
  circulationDepth: number;
  totalFlowArea: number;
  useMudPits: boolean;
  mudPits: any;
  annulusIndex: number;
}

@Component({
    selector: 'app-trip-pipe-and-circulate-details',
    templateUrl: './trip-pipe-and-circulate-details.component.html',
    providers: subformComponentProviders(TripPipeAndCirculateDetailsComponent),
    standalone: false
})
export class TripPipeAndCirculateDetailsComponent implements OnChanges, OnDestroy, AfterViewInit {

  constructor(
    private _drillstringService: DrillstringService,
    private _signalRService: SignalRService
  ) { }

  private _fluids: Fluid[] = [];

  @Input() set fluids(value: Fluid[]) {
    this._fluids = value?.filter(f => 
      f.state.type === FluidType.BRINE ||
      f.state.type === FluidType.STANDARDMUD ||
      f.state.type === FluidType.ADVANCEDMUD
    )?.sort((a, b) => a.state.name.localeCompare(b.state.name)) ?? [];
  }

  get fluids(): Fluid[] {
    return this._fluids;
  }

  private _annularFluidId: string = null;
  @Input() set annularFluidId(value: string) {
    this._annularFluidId = value ?? null;
  }
  get annularFluidId(): string {
    return this._annularFluidId;
  }

  @Input()
  private wellConfig: any;

  @Input()
  public isReadOnly: boolean;

  @Input()
  public isSubsea: boolean;

  @Input()
  public surfaceTemperature: number;

  @Input()
  public userUnits: UserUnitsModel;

  @Input()
  public currentString: TubularString;

  @Input()
  public currentTubularIndex: number;

  @Input()
  public previousOperationMudPits: MudPit;

  @Input()
  public triggerMudPitTempChange: boolean;

  @Output()
  selectedTabChange = new EventEmitter<string>();

  public operationType = 'tripPipeAndCirculate';
  public FluidType: typeof FluidType = FluidType;
  public isLoading = signal<boolean>(true);
  public showDrillString: boolean;
  public drillStringMissing: boolean;
  public selectedHwdp: string;
  public hasHwdp: boolean;
  public hasCollar: boolean;
  public selectedCollar: string;
  public selectedDrillPipe: string;
  public poohDisabled: boolean;
  public showRiser: boolean;
  public currentShoe: number;
  public drillPipe: any;
  public hwdp: any;
  public collar: any;
  public tempUnit: string;
  public shortLengthUnit: string;
  public pipeRoughnessUnit: string;
  public weigthUnit: string;
  public temperatureValidation: { min: number, max: number };
  public pipeRoughnessValidation: { min: number, max: number };
  public areaUnit: string;
  public totalFlowAreaValidation: { min: number, max: number };
  public deepestShoe: number;
  public useMudPits = false;
  
  private _defaultPipeRoughness: number;
  private _dataBackup: any | null = null;

  public form = createForm<TripPipeAndCirculate, TripPipeAndCirculateForm>(this, {
    formType: FormType.SUB,
    formControls: {
      type: new UntypedFormControl(OperationType.TRIPPIPEANDCIRCULATE),
      fluid: new UntypedFormControl(null, [Validators.required]),
      standardFluidFlow: new UntypedFormControl(null, [Validators.required]),
      tripIntoHole: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required, Validators.min(0), Validators.max(100000)] }),
      includeRiser: new UntypedFormControl(null),
      outsideDiameter: new UntypedFormControl(null,  { updateOn: 'blur' }),
      insideDiameter: new UntypedFormControl(null, { updateOn: 'blur' }),
      pipeRoughness: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required] }),
      circulationTime: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required] }),
      isPullOutOfHole: new UntypedFormControl(false, { updateOn: 'blur', validators: [Validators.required] }),
      inletTemperature: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required] }),
      isNextStringInPlace: new UntypedFormControl(false, [Validators.required]),
      circulationDepth: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required] }),
      pullOutOfHoleTime: new UntypedFormControl(0, { updateOn: 'blur', validators: [Validators.required, Validators.min(0), Validators.max(100000)] }),
      totalFlowArea: new UntypedFormControl(null, { updateOn: 'blur', validators: [Validators.required] }),
      useMudPits: new UntypedFormControl(false),
      mudPits: new UntypedFormControl(null),
      annulusIndex: new UntypedFormControl(null)
    },
    toFormGroup: (obj: TripPipeAndCirculate | null): TripPipeAndCirculateForm | null => {
      if (!obj) {
        return null;
      }

      this._dataBackup = { ...obj };

      const { tripIntoHole, circulationTime, pipeRoughness, riser, inletTemperature, includeRiser, useMudPits, mudPits,
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        isPullOutOfHole, isNextStringInPlace, pullOutOfHoleTime, totalFlowArea } = obj;

      obj.fluid = this.fluids?.find(f => f.id === obj.fluid.id);

      this.poohChange(isPullOutOfHole);
      this.showRiser = includeRiser;
      this.useMudPits = useMudPits;

      return {
        type: obj.type,
        circulationTime,
        isNextStringInPlace,
        isPullOutOfHole,
        tripIntoHole,
        pipeRoughness,
        includeRiser: includeRiser,
        outsideDiameter: riser?.outsideDiameter,
        insideDiameter: riser?.insideDiameter,
        circulationDepth: obj.circulationDepth,
        pullOutOfHoleTime,
        totalFlowArea,
        fluid: obj.fluid,
        inletTemperature,
        standardFluidFlow: obj.flow,
        useMudPits: useMudPits,
        mudPits: useMudPits ? mudPits : null,
        annulusIndex: obj.annulusIndex
      };
    },
    fromFormGroup: (formValue: TripPipeAndCirculateForm): TripPipeAndCirculate | null => {

      const { fluid, tripIntoHole, standardFluidFlow, pipeRoughness, outsideDiameter, insideDiameter,
         circulationTime, circulationDepth, inletTemperature, isPullOutOfHole, isNextStringInPlace, 
         // eslint-disable-next-line @typescript-eslint/no-unused-vars
         pullOutOfHoleTime, totalFlowArea, annulusIndex, includeRiser, useMudPits, mudPits } = formValue;

      const controls = this.form.formGroup.controls;
      const check = (control, field) => control?.invalid || (control?.touched && this._dataBackup?.[field] === formValue[field]);
        
      if (check(controls.tripIntoHole, 'tripIntoHole') || 
          check(controls.inletTemperature, 'inletTemperature') ||
          check(controls.pullOutOfHoleTime, 'pullOutOfHoleTime') ||
          check(controls.circulationDepth, 'circulationDepth') ||
          check(controls.standardFluidFlow, 'standardFluidFlow') ||
          check(controls.circulationTime, 'circulationTime')) {
        
        controls.tripIntoHole.markAsUntouched();
        controls.inletTemperature.markAsUntouched();
        controls.pullOutOfHoleTime.markAsUntouched();
        controls.circulationDepth.markAsUntouched();
        controls.standardFluidFlow.markAsUntouched();
        controls.circulationTime.markAsUntouched();
        return;
      }

      this._dataBackup = formValue;

      return {
        type: OperationType.TRIPPIPEANDCIRCULATE,
        flow: standardFluidFlow,
        fluid,
        tripIntoHole,
        circulationTime,
        inletTemperature,
        pipeRoughness,
        isPullOutOfHole,
        includeRiser,
        riser: this.showRiser ? {
          outsideDiameter,
          insideDiameter
        } : null,
        isNextStringInPlace,
        pullOutOfHoleTime,
        circulationDepth,
        totalFlowArea,
        annulusIndex,
        useMudPits,
        mudPits
      };
    }
  });

  async ngOnChanges(): Promise<void> {
    this.isLoading.set(true);

    this.tempUnit = this.userUnits.temperature;
    this.shortLengthUnit = this.userUnits.shortLengths == 'in' ? '"' : this.userUnits.shortLengths;
    this.weigthUnit = this.userUnits.linearDensity == 'lb/ft' ? '#' : this.userUnits.linearDensity;
    this.areaUnit = this.userUnits.area;
    this.pipeRoughnessUnit = this.userUnits.shortLengths;

    switch (this.tempUnit) {
      case '°F':
        this.temperatureValidation = { min: 0, max: 500 };
        break;
      case '°C':
        this.temperatureValidation = { min: -17.77, max: 260 };
        break;
      case 'K':
        this.temperatureValidation = { min: 255.37, max: 533.15 };
        break;
    }
    this.form.formGroup.controls.inletTemperature.setValidators([Validators.min(this.temperatureValidation.min), Validators.max(this.temperatureValidation.max), Validators.required]);

    this._defaultPipeRoughness = 0.001968;
    if (this.userUnits.shortLengths == 'in') {
      this.pipeRoughnessValidation = { min: 0, max: 0.25 };
    } else if (this.userUnits.shortLengths == 'cm') {
      this._defaultPipeRoughness = 0.00499;
      this.pipeRoughnessValidation = { min: 0, max: 0.6 };
    } else {
      this._defaultPipeRoughness = 0.0499;
      this.pipeRoughnessValidation = { min: 0, max: 6.3 };
    }
    this.form.formGroup.controls.pipeRoughness.setValidators([Validators.min(this.pipeRoughnessValidation.min), Validators.max(this.pipeRoughnessValidation.max), Validators.required]);

    if (this.areaUnit == 'in²') {
      this.totalFlowAreaValidation = { min: 0, max: 100 };
    } else {
      this.totalFlowAreaValidation = { min: 0, max: 254 };
    }
    this.form.formGroup.controls.totalFlowArea.setValidators([Validators.min(this.totalFlowAreaValidation.min), Validators.max(this.totalFlowAreaValidation.max), Validators.required]);

    const hub = this._signalRService.getConnectionToNotificationHub();
    this._signalRService.subscribeToEventFilteredByDesignId(hub, SignalRService.ON_PFB_CHANGE, d => this.signalRfunc(d));

    this.getTripPipeData();
  }

  signalRfunc(data: any){
    if ((data.tubularStringId && data.tubularStringId !== this.currentString.id)) {
      return;
    }
    if (data.action === PeriforOnChangeMessages.REFRESH_DRILL_STRING) {
      this.getTripPipeData();
    }
  }

  ngAfterViewInit(): void {
    if (this.form.formGroup.controls.pipeRoughness.value == null) {
      this.form.formGroup.controls.pipeRoughness.patchValue(this._defaultPipeRoughness, { emitEvent: false });
    }
    const defaultFluid = this.fluids ? this.fluids.find(x => x.id === this.annularFluidId) : null;
    if (!this.form.formGroup.controls.fluid.value) {
      this.form.formGroup.controls.fluid.setValue(defaultFluid, { emitEvent: false });
    }
  }

  private getTripPipeData() {
    this.isLoading.set(true);
    this._drillstringService.getDrillstringForTubular().pipe(
      map((drillstring) => {
        this.drillPipe = drillstring['drillPipe'];
        this.hwdp = drillstring['heavyWeightDrillPipe'];
        this.collar = drillstring['collar'];
        this.drillStringMissing = !this.drillPipe && (drillstring.hwdpLength ?? 0) == 0 && (drillstring.collarLength ?? 0) == 0;

        this.currentShoe = this.currentString.shoeMd;

        const drillPipeLength = +((this.form.formGroup.controls.circulationDepth.value ?? this.currentShoe) - drillstring.collarLength - drillstring.hwdpLength).toFixed(2);
        this.selectedDrillPipe = this.drillPipe ?
          `Drill String: OD: ${this.drillPipe.outsideDiameter}${this.shortLengthUnit}, Weight: ${this.drillPipe.weightPerFoot.toFixed(2)}${this.weigthUnit}, ID: ${this.drillPipe.insideDiameter}${this.shortLengthUnit}, Grade: ${drillstring.drillPipeGrade?.name || 'Steel'}, Length: ${drillPipeLength} ${this.userUnits.longLengths}` : null;
        this.selectedHwdp = this.hwdp ? `HWDP: OD: ${this.hwdp.outsideDiameter}${this.shortLengthUnit}, Weight: ${this.hwdp.weightPerFoot.toFixed(2)}${this.weigthUnit}, ID: ${this.hwdp.insideDiameter}${this.shortLengthUnit}, Length: ${drillstring.hwdpLength.toFixed(2)} ${this.userUnits.longLengths}` : null;
        this.selectedCollar = this.collar ? `Collar: OD: ${this.collar.outsideDiameter}${this.shortLengthUnit}, Weight: ${this.collar.weightPerFoot.toFixed(2)}${this.weigthUnit}, ID: ${this.collar.insideDiameter}${this.shortLengthUnit}, Length: ${drillstring.collarLength.toFixed(2)} ${this.userUnits.longLengths}` : null;
        this.hasHwdp = drillstring.hwdpLength > 0;
        this.hasCollar = drillstring.collarLength > 0;

        if (!this.hasCollar && !this.hasHwdp && !this.drillPipe) {
          this.selectedDrillPipe = 'Please specify a drill string';
        }

        const hasRiserDefault = this.isSubsea && this.currentTubularIndex > 0 ? true : false;
        const riserControl = this.form.formGroup.controls.includeRiser;
        if (riserControl.value === null) {
          riserControl.setValue(hasRiserDefault, { emitEvent: false });
          this.hasRiserChange();
        }

        this.form.formGroup.controls.circulationDepth.setValue((this.form.formGroup.value.circulationDepth ?? this.currentShoe), { emitEvent: false });

        this.deepestShoe = Math.max(...this.wellConfig.map(t => t.shoeMd));
        this.form.formGroup.controls.circulationDepth.setValidators([Validators.max(this.deepestShoe)]);

        this.form.formGroup.controls.inletTemperature
          .setValue((this.form.formGroup.value.inletTemperature ?? this.surfaceTemperature), { emitEvent: false });

        this.form.formGroup.controls.totalFlowArea
          .setValue((this.form.formGroup.value.totalFlowArea ? this.form.formGroup.value.totalFlowArea : this.areaUnit == 'in²' ? 1 : 6.45), { emitEvent: false });

        this.form.formGroup.controls.outsideDiameter
        .setValue((this.form.formGroup.value.outsideDiameter ?? GetShortLengthValueFromInches(21, this.userUnits.shortLengths)), { emitEvent: false });

        this.form.formGroup.controls.insideDiameter
          .setValue((this.form.formGroup.value.insideDiameter ?? GetShortLengthValueFromInches(19.75, this.userUnits.shortLengths)), { emitEvent: false });

        this.poohChange(this.form.formGroup.controls.isPullOutOfHole);
        this.isLoading.set(false);
      }),
      catchError(err => {
        this.isLoading.set(false);
        return err;
      })).subscribe();
  }

  public poohChange(e: any): void {
    const checked = e?.checked ?? e?.value ?? e;
    if (checked) {
      this.poohDisabled = false;
    } else {
      this.poohDisabled = true;
    }

    if (!checked && this.form.formGroup.controls.pullOutOfHoleTime.value > 0) {
      this.form.formGroup.controls.pullOutOfHoleTime.setValue(0);
    } else if (checked && this.form.formGroup.controls.pullOutOfHoleTime.value == 0) {
      this.form.formGroup.controls.pullOutOfHoleTime.setValue(10);
    }
  }

  public hasRiserChange() {
    this.showRiser = this.form.formGroup.controls.includeRiser.value ?? false;

    if (this.showRiser) {
      this.form.formGroup.controls.outsideDiameter.setValidators([Validators.required]);
      this.form.formGroup.controls.insideDiameter.setValidators([Validators.required]);
    } else {
      this.form.formGroup.controls.outsideDiameter.clearValidators();
      this.form.formGroup.controls.insideDiameter.clearValidators();
    }

    this.form.formGroup.controls.outsideDiameter.updateValueAndValidity();
    this.form.formGroup.controls.insideDiameter.updateValueAndValidity();

    this.checkRiserValues();
  }

  private checkRiserValues() {
    this.form.formGroup.controls.outsideDiameter
      .setValue((this.form.formGroup.value.outsideDiameter ?? GetShortLengthValueFromInches(21, this.userUnits.shortLengths)), { emitEvent: false });

    this.form.formGroup.controls.insideDiameter
      .setValue((this.form.formGroup.value.insideDiameter ?? GetShortLengthValueFromInches(19.75, this.userUnits.shortLengths)));
  }

  ngOnDestroy(): void {
    this.signalRfunc = null
  }
}
